import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { ThreeDimensionalInput } from "@/components/form-items/DimensionsInput";
import {
  SimplifiedFicheLayout,
  SimplifiedSection,
} from "@/components/form-items/SimplifiedFicheLayout";
import MediaLibrary, { ImageFormData } from "@/components/images/MediaLibrary";
import { Form } from "@/components/ui/form";
import { getMainLanguage } from "@/i18n";
import { tWithKey } from "@/lib/i18n-fallback";
import { TypeOptionPosition } from "@/lib/image";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type ArtworkSimplifiedViewProps = {
  form: UseFormReturn<FieldValues>;
  /** Options for the "Artiste / auteur" multi-select. */
  authorOptions: { label: string; value: string }[];
  /** Pro-view tab labels, used to size the left column like the complete view. */
  tabLabels?: string[];
  /** Everything the inline (simplified) photo manager needs. */
  media: {
    id: string;
    type: TypeOptionPosition;
    computeImageName?: (data?: ImageFormData) => string;
    invalidateOnChange?: () => void;
    embedAttachmentOnSubmit?: boolean;
  };
};

/**
 * Simplified ("Essentiel") body of the artwork sheet. It reuses the exact same
 * field components as the complete/"Pro" view (so lists stay editable, etc.) but
 * lays out only the essential fields, ignoring the per-field masking grid (the
 * enclosing FormFieldVisibilityProvider is mounted with `forceAllVisible`).
 *
 * The structure — a per-sheet body plugged into the shared
 * {@link SimplifiedFicheLayout} — is meant to be replicated for other sheets.
 */
export function ArtworkSimplifiedView({
  form,
  authorOptions,
  tabLabels,
  media,
}: ArtworkSimplifiedViewProps) {
  const { t } = useTranslation();
  const lang = getMainLanguage();

  return (
    <Form {...form}>
      <SimplifiedFicheLayout tabLabels={tabLabels}>
        <SimplifiedSection
          title={tWithKey(
            t,
            "artwork.simplified.section.identification",
            "Identification",
          )}
        >
          <AF.Row className="items-start [&>*]:flex-1">
            <AF.Text name="oeuvresDescriptionNumeroEnregistrement" nonMaskable />
            <AF.Text name="oeuvresDescriptionTitre" nonMaskable />
          </AF.Row>
          <AF.Row className="items-start [&>*]:flex-1">
            <ModifiableList
              name={`oeuvresDescriptionTypeOeuvreMultilingual.${lang}`}
              label={t("oeuvresDescriptionTypeOeuvreMultilingual")}
              listKey="liste-types-oeuvres"
              lang={lang}
            />
            <AF.Text name="oeuvresDescriptionDateDeCreation" />
          </AF.Row>
          <AF.MultiSelectField
            name="relatedAuthorIds"
            options={authorOptions}
            label={t("estampesMultiplesDescriptionAuteurArtiste")}
            placeholder={t("select")}
          />
        </SimplifiedSection>

        <SimplifiedSection
          title={tWithKey(
            t,
            "artwork.simplified.section.description",
            "Description",
          )}
        >
          <AF.Row className="items-start [&>*]:flex-1">
            <ModifiableList
              name={`oeuvresDescriptionTechnique.${lang}`}
              label={t("oeuvresDescriptionTechnique")}
              listKey="liste-techniques"
              lang={lang}
            />
            <ModifiableList
              name={`oeuvresDescriptionSupport.${lang}`}
              label={t("oeuvresDescriptionSupport")}
              listKey="liste-supports"
              lang={lang}
            />
          </AF.Row>
          <ThreeDimensionalInput
            parentName="oeuvresDescriptionDimensionsOeuvre"
            cmOnly
            label={tWithKey(
              t,
              "artwork.simplified.section.dimensions",
              "Dimensions en centimètres",
            )}
            sublabel={tWithKey(
              t,
              "artwork.simplified.section.dimensionsAxes",
              "Hauteur × Largeur × Profondeur",
            )}
          />
          <ModifiableList
            name={`oeuvresDescriptionSignatureLocalisation.${lang}`}
            label={tWithKey(t, "artwork.simplified.section.signature", "Signature")}
            listKey="liste-signatures"
            lang={lang}
          />
        </SimplifiedSection>

        <SimplifiedSection
          hideFieldLabels
          title={tWithKey(
            t,
            "artwork.simplified.section.longDescription",
            "Description longue de l'œuvre",
          )}
        >
          <AF.RichText name="oeuvresDescriptionDescriptionLongue" />
        </SimplifiedSection>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <SimplifiedSection
            hideFieldLabels
            title={tWithKey(t, "artwork.simplified.section.price", "Prix")}
          >
            <AF.SelectCurrency name="oeuvreVenteMontant" />
          </SimplifiedSection>
          <SimplifiedSection
            hideFieldLabels
            title={tWithKey(t, "artwork.simplified.section.storage", "Rangement")}
          >
            <ModifiableList
              name="oeuvresLocalisationHistoriqueLieuDeStockage"
              listKey="liste-lieux-stockage"
            />
          </SimplifiedSection>
          <SimplifiedSection
            hideFieldLabels
            title={tWithKey(t, "artwork.simplified.section.status", "Statut")}
          >
            <ModifiableList name="oeuvresStatus" listKey="liste-status" />
          </SimplifiedSection>
        </div>

        <SimplifiedSection
          title={tWithKey(t, "artwork.simplified.section.photos", "Photos")}
        >
          <MediaLibrary
            simplified
            id={media.id}
            type={media.type}
            computeImageName={media.computeImageName}
            invalidateOnChange={media.invalidateOnChange}
            embedAttachmentOnSubmit={media.embedAttachmentOnSubmit}
            setImageMode={() => {}}
          />
        </SimplifiedSection>
      </SimplifiedFicheLayout>
    </Form>
  );
}

export default ArtworkSimplifiedView;
